import React, { useContext, useState } from "react";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { useTranslation } from "../../context/Internationalization";
import { AuthContext } from "../../context/AuthContext";
import {
  GET_USER_PROFILES_SA,
  SEARCH_USER_WITH_IDENTITY_SA,
} from "./SuperAdminService";
import { Box, Button, Grid } from "@mui/material";
import boxStyles from "../../styles/boxing";
import Stack from "@mui/material/Stack";
import MainTitle from "../../Components/Common/MainTitle/MainTitle";
import Divider from "@mui/material/Divider";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import { GET_RESOLVE_USERS, GET_USER } from "../../graphql/userGraphql";
import ImpersonateConfirmationModal from "./UsersTable/ImpersonateConfirmationModal";
import { useSuperAdmin } from "../../context/SuperAdminContext";
import { getStorage } from "../../auth/utils";
import { useNavigate } from "react-router-dom";
import Form from "../../Components/Form/Form";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import useStyles from "../Forms/formsStyles";
import ProfilesTable from "./ProfilesTable/ProfilesTable";
import TeamsTable from "./TeamsTable/TeamsTable";
import UserInvitationsTable from "./UserInvitations/UserInvitationsTable";
import UserClubTable from "./UserClubTable/UserClubTable";
import { SnackBarContext } from "../../context/SnackBarContext";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { USER_ROLES } from "../../utility/Enum";
import {
  endImpersonationSession,
  setImpersonatedActiveClub,
  suspendActiveClubSelection,
} from "../../auth/impersonationClub";

const schema = (t) => [
  {
    name: "firstName",
    label: t("firstName"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "lastName",
    label: t("lastName"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "mobile",
    label: t("mobile"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "email",
    label: t("email"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
];

const initialState = {
  user: null,
  loading: false,
};

const SuperAdminImpersonate = (props) => {
  const { t, switchLanguage } = useTranslation();
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState({ id: "", identity: "" });
  const { setImpersonate } = useContext(AuthContext);
  const { setUser } = useSuperAdmin();
  const navigate = useNavigate();
  const styles = useStyles();
  const snackBar = useContext(SnackBarContext);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const handleImpersonate = () => {
    setState({ ...state, impersonateModal: true });
  };

  const normalizeIdentity = (identity) => {
    const trimmedIdentity = identity?.trim();

    if (!trimmedIdentity) {
      return "";
    }

    if (trimmedIdentity.includes("@")) {
      return trimmedIdentity.toLowerCase();
    }

    return trimmedIdentity.replace(/\s/g, "").replace(/\+/g, "");
  };

  const searchByIdentity = (identity) => {
    const normalizedIdentity = normalizeIdentity(identity);

    if (!normalizedIdentity) {
      setState((prevState) => ({ ...prevState, loading: false }));
      return;
    }

    axiosGraphQL({
      query: SEARCH_USER_WITH_IDENTITY_SA,
      variables: { identity: normalizedIdentity },
    })
      .then((response) => {
        const user =
          response?.data?.data?.searchUserWithIdentitySA &&
          response?.data?.data?.searchUserWithIdentitySA[0];
        if (user) {
          if (!user._id) {
            const message =
              "Unable to load profiles because the user ID is missing.";
            setState((prevState) => ({ ...prevState, loading: false }));
            snackBar.setOptions({
              alertMessage: message,
              alertSeverity: "error",
            });
            snackBar.setSnackbarOpen(true);
            return;
          }

          axiosGraphQL({
            query: GET_USER_PROFILES_SA,
            variables: { userId: user._id },
          })
            .then((response) => {
              setState({
                user: user,
                profiles: response?.data?.data?.getClubProfilesSA,
              });
            })
            .catch((error) => {
              errorLog(
                error,
                "SuperAdminImpersonate",
                "searchByIdentity",
                "GET_USER_PROFILES_SA"
              );
              setState((prevState) => ({ ...prevState, loading: false }));
              snackBar.setOptions({
                alertMessage: error.message,
                alertSeverity: "error",
              });
              snackBar.setSnackbarOpen(true);
            });
        } else {
          setState((prevState) => ({ ...prevState, loading: false }));
        }
      })
      .catch((error) => {
        errorLog(
          error,
          "SuperAdminImpersonate",
          "resolveUser",
          "SEARCH_USER_WITH_IDENTITY_SA"
        );
        setState((prevState) => ({ ...prevState, loading: false }));
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      });
  };

  const resolveUser = () => {
    setState((previousState) => ({
      ...previousState,
      loading: true,
      user: null,
    }));
    if (filters.identity && filters.identity.length > 0) {
      searchByIdentity(filters.identity);
    } else if (filters.id && filters.id.length > 0) {
      axiosGraphQL({
        query: GET_RESOLVE_USERS,
        variables: {
          ids: [filters.id],
          skip: 0,
          limit: 20,
        },
      })
        .then((response) => {
          const { resolveUserIds } = response.data.data;
          const resolvedUser = resolveUserIds[0];
          searchByIdentity(resolvedUser?.email || resolvedUser?.mobile);
        })
        .catch((error) => {
          errorLog(
            error,
            "SuperAdminImpersonate",
            "resolveUser",
            "GET_RESOLVE_USERS"
          );
          setState((prevState) => ({ ...prevState, loading: false }));
          snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        });
    }
  };

  const onChangeFilter = ({ target: { name, value } }) => {
    setFilters((prevState) => ({ ...prevState, [name]: value }));
  };

  const onImpersonate = () => {
    const storage = getStorage();
    suspendActiveClubSelection(storage);
    const rollbackImpersonation = () => {
      endImpersonationSession(storage);
      setUser(null);
      setImpersonate(null);
    };

    setUser(state.user);
    setTimeout(() => {
      axiosGraphQL({ query: GET_USER })
        .then(async (response) => {
          const me = response?.data?.data?.me;
          if (!me?.club || !me?.clubs || !me?.user) {
            throw new Error("Impersonated user does not manage a club.");
          }

          const user = me.user;
          const userLangDB = user.language;
          const userLangLS = storage.getItem("language");
          if (userLangDB !== userLangLS) {
            switchLanguage(userLangDB);
          }
          user.club = me.club;
          user.clubs = me.clubs;
          setImpersonatedActiveClub(storage, user.club?._id);
          storage.setItem("impersonate", JSON.stringify(user));
          setImpersonate(user);
          navigate("/");
        })
        .catch((error) => {
          rollbackImpersonation();
          errorLog(error, "SuperAdminImpersonate", "onImpersonate", "GET_USER");
          snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        });
    }, 100);
  };

  const onCloseModal = () => {
    setState({ ...state, impersonateModal: false });
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Box>
        <ImpersonateConfirmationModal
          open={state.impersonateModal}
          onClose={onCloseModal}
          onImpersonate={onImpersonate}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={boxStyles.headerContainer} component="div">
              <Stack direction={"column"} spacing={1}>
                <MainTitle title={t("search_user")} />
              </Stack>
            </Box>
            <Divider style={{ width: "100%" }} />
          </Grid>
          <Grid item xs={12} style={boxStyles.filtersContainer}>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              spacing={1}
            >
              <Stack direction={"row"} spacing={1} alignItems={"end"}>
                <Grid item xs={4}>
                  <TextFieldRenderer
                    label={"Id"}
                    onChange={onChangeFilter}
                    name={"id"}
                    value={filters.id}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextFieldRenderer
                    label={t("email_or_mobile")}
                    onChange={onChangeFilter}
                    name={"identity"}
                    value={filters.identity}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    startIcon={<PersonSearchOutlinedIcon />}
                    type="button"
                    size="small"
                    onClick={resolveUser}
                  >
                    {t("search_user")}
                  </Button>
                </Grid>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {state.user && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction={"row"} justifyContent={"flex-end"}>
                <Button
                  startIcon={<ManageAccountsOutlinedIcon />}
                  onClick={handleImpersonate}
                  disabled={!state.user?.roles?.includes(USER_ROLES.CLUB_ADMIN)}
                >
                  Impersonate
                </Button>
              </Stack>
              <Form schema={schema(t)} values={state.user || {}} />
            </Grid>
            <Grid item xs={12}>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("clubs")}
                </AccordionSummary>
                <AccordionDetails>
                  <UserClubTable
                    data={state.user?.clubs || []}
                    emptyMessage={t("no_clubs_found")}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("teams")}
                </AccordionSummary>
                <AccordionDetails>
                  <TeamsTable
                    data={
                      state?.user?.teams?.map((team) => ({
                        ...team,
                        userId: state.user?._id,
                      })) || []
                    }
                    emptyMessage={t("no_teams_found")}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("user_invitations")}
                </AccordionSummary>
                <AccordionDetails>
                  <UserInvitationsTable
                    data={state?.user?.invitations || []}
                    emptyMessage={t("no_notifications_found")}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("profiles")}
                </AccordionSummary>
                <AccordionDetails>
                  <ProfilesTable
                    data={state.profiles ? state.profiles : []}
                    emptyMessage={t("no_players_found")}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

SuperAdminImpersonate.propTypes = {};

export default SuperAdminImpersonate;
